import {Injectable} from '@angular/core';
import {ComplianceItemApi} from '../api/compliance-item.api';
import {CplItem} from '../model/cpl-item.model';
import {UnitOfMeasure} from '../supervision-management/compliance-items/compliance-item/enums/unit-of-measure.enum';
import {UserAssignmentRequest} from '../model/user-assignment-request.model';
import {CplCycle} from '../model/cpl-cycle.model';
import { finalize } from 'rxjs/operators';
import { CplItemDuplicateData } from '../model/cpl-item-duplicate-data.model';
import { SessionService } from '@edl-development/edl-common';
import { PagedModel } from '../model/common/paged.model';
import { CplItemStatusSearch } from '../enums/cpl-item-status-search.enum';
import { CplItemSubmissionStatusFilter } from '../supervision-management/enums/cpl-item-submission-status-filter.enum';

@Injectable()
export class ComplianceItemService {

    constructor(
        private complianceItemApi: ComplianceItemApi,
        private sessionService:SessionService
    ) {}

    getComplianceItem = (cplId: string, cycleID: string) => {
        this.sessionService.AppLoading(true);

        return this.complianceItemApi.getComplianceItem(cplId, cycleID)
            .pipe(
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    getMyComplianceItems = (offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, filterStatusBy?: CplItemStatusSearch, filterByTag?:string, filterByItemPacket?: string[], submissionStatus?: CplItemSubmissionStatusFilter) => {
        this.sessionService.AppLoading(true);

        // if status is template, then don't filter by item packets
        const adjustedItemPackets = filterStatusBy === CplItemStatusSearch.TEMPLATE ? [] : filterByItemPacket;

        return this.complianceItemApi.getMyComplianceItems(offset, limit, orderBy, ascending, searchBy, filterStatusBy, filterByTag, adjustedItemPackets, submissionStatus)
            .pipe(
                finalize(() => this.sessionService.AppLoading(false))
            )
    }

    getComplianceItemsAssignedToUser(userId: string) {
        this.sessionService.AppLoading(true);

        return this.complianceItemApi.getComplianceItemsAssignedToUser(userId)
            .pipe(
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    getComplianceItems(offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, status?: CplItemStatusSearch, tag?:number, itemPackets?:string[], submissionStatus?: CplItemSubmissionStatusFilter) {
        this.sessionService.AppLoading(true);

        // if status is template, then don't filter by item packets
        const adjustedItemPackets = status === CplItemStatusSearch.TEMPLATE ? [] : itemPackets;

        return this.complianceItemApi.getComplianceItems(offset, limit, orderBy, ascending, searchBy, status, tag, adjustedItemPackets, submissionStatus)
            .pipe(
                finalize(() => this.sessionService.AppLoading(false))
            );
    }

    getNewItemPermissions = () => this.complianceItemApi.getNewItemPermissions();

    createComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.createComplianceItem(complianceItem);

    updateComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.updateComplianceItem(complianceItem);

    deleteComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.deleteComplianceItem(complianceItem);

    restoreComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.restoreComplianceItem(complianceItem);

    removeComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.removeComplianceItem(complianceItem);

    archiveComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.archiveComplianceItem(complianceItem);

    getArchiveWarnings = (cplId: string) => this.complianceItemApi.getArchiveWarnings(cplId);

    activateComplianceItem = (complianceItem: CplItem) => this.complianceItemApi.activateComplianceItem(complianceItem);

    convertComplianceItemToTemplate = (complianceItemDuplicateData: CplItemDuplicateData) => 
        this.complianceItemApi.convertComplianceItemToTemplate(complianceItemDuplicateData);

    duplicateComplianceItem = (complianceItemDuplicateData: CplItemDuplicateData) => 
        this.complianceItemApi.duplicateComplianceItem(complianceItemDuplicateData);

    requestAccess = (cplItemId: string) => this.complianceItemApi.requestAccess(cplItemId);

    getItemUserAssignments = ({
        cplId, 
        cycleId, 
        offset, 
        limit, 
        orderBy, 
        ascending, 
        searchBy, 
        statusId, 
        a48statusId,
        approverUserId,
        groupIds
    } : {
        cplId: string, 
        cycleId: string, 
        offset: number, 
        limit: number, 
        orderBy?: string, 
        ascending?: boolean,
        searchBy?:string, 
        statusId?:number, 
        a48statusId?:number,
        approverUserId?:string,
        groupIds?:string[]
    }) => this.complianceItemApi.getItemUserAssignments({cplId, cycleId, offset, limit, orderBy, ascending, searchBy, statusId: statusId ? statusId : -1, a48statusId, approverUserId, groupIds });

    getUserUserAssignments = ({
        cplId, 
        offset, 
        limit, 
        orderBy, 
        ascending,
        searchBy, 
        statusId, 
        a48statusId,
        approverUserId,
        groupIds
    } : { 
        cplId: string,
        offset: number, 
        limit: number, 
        orderBy?: string, 
        ascending?: boolean,
        searchBy?:string, 
        statusId?:number, 
        a48statusId?:number,
        approverUserId?:string,
        groupIds?:string[]
    }) => this.complianceItemApi.getUserUserAssignments({ cplId, offset, limit, orderBy, ascending, searchBy, statusId: statusId ? statusId : -1, a48statusId, approverUserId, groupIds });

    removeUserAssignments = (request: UserAssignmentRequest) => this.complianceItemApi.removeUserAssignments(request);

    createUserAssignments = (request: UserAssignmentRequest) => this.complianceItemApi.createUserAssignments(request);

    updateUserAssignments = (userCycles: CplCycle[]) => this.complianceItemApi.updateUserAssignments(userCycles);

    getUserSummary = (userId: string, paging: PagedModel, itemPackets: string[] = []) =>
        this.complianceItemApi.getUserSummary(userId, paging, itemPackets);

    getExpiredCycles = (cplId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) =>
        this.complianceItemApi.getExpiredCycles(cplId, offset, limit, orderBy, ascending, searchBy, baseUrl);

    getUserCycleHistory = (cplId: string, userId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) =>
        this.complianceItemApi.getUserCycleHistory(cplId, userId, offset, limit, orderBy, ascending, searchBy, baseUrl);

    getUserCycleHistoryByCycle = (cycleId: string,  offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) =>
        this.complianceItemApi.getUserCycleHistoryByCycle(cycleId, offset, limit, orderBy, ascending, searchBy, baseUrl);

    getUserCompliance = (paging: PagedModel, groups: string[] = []) =>
        this.complianceItemApi.getUserCompliance(paging, groups);

    getUserStatusSummary = (userId: string) => this.complianceItemApi.getUserStatusSummary(userId);

    getTags = (searchBy: string) => this.complianceItemApi.getTags(searchBy);

    createBulkUserAssignments = (fileList: FileList, ItemID: string) => this.complianceItemApi.createBulkUserAssignments(fileList, ItemID);

    createBulkSingleUserToMultipleAssignments = (requests: UserAssignmentRequest[]) => this.complianceItemApi.createBulkSingleUserToMultipleAssignments(requests);

    unitOfMeasure(uom: number): string {
        let retval;

        if (typeof uom === 'string') {
            uom = parseInt(uom);
        }

        switch (uom) {
            case UnitOfMeasure.DAY:
                retval = ' Day';
                break;

            case UnitOfMeasure.WEEK:
                retval = ' Week';
                break;

            case UnitOfMeasure.MONTH:
                retval = ' Month';
                break;

            case UnitOfMeasure.YEAR:
                retval = ' Year';
                break;
        }

        return retval;
    } 

    getPrintableRoster = (complianceItemService) => this.complianceItemApi.getPrintableRoster(complianceItemService);

    getUserArchiveSummary = (userId: string, updateStatusCounts:boolean=true) => this.complianceItemApi.getUserArchiveSummary(userId);
}